// src/components/Sidebar.tsx
"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRecordContext } from "@/contexts/RecordContext";
import SidebarHeader from "@/components/Sidebar/SidebarHeader";
import SidebarKaitouMonitorLink from "@/components/Sidebar/SidebarKaitouMonitorLink";
import SidebarCourses from "@/components/Sidebar/SidebarCourses";
import SidebarCardType from "@/components/Sidebar/SidebarCardType";
import SidebarHowTo from "@/components/Sidebar/SidebarHowTo";
import SidebarTopics from "@/components/Sidebar/SidebarTopics";
import SidebarLegal from "@/components/SidebarLegal";
import { useStripePlanStatus } from "@/hooks/useStripePlanStatus";
import { useHowtoCanPost } from "@/hooks/useHowto";
// ▼ 追加：override/実効プランを使ったUI判定
import { isOverrideActive, getEffectivePlan } from "@/lib/planMap";
import { useEventLogger } from "@/lib/useEventLogger";

export default function Sidebar() {
  const recordContext = useRecordContext();
  if (!recordContext) return null;

  const {
    userProfile,
    sidebarCollapsed,
    setSidebarCollapsed,
    openSection,
    setOpenSection,
    sessionMode,
    setSessionMode,
  } = recordContext;

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const { log: logEvent } = useEventLogger();

  const handleModeSwitch = (to: "card" | "text") => {
    if (to === sessionMode) return;
    logEvent("mode_switch", { from: sessionMode, to });
    setSessionMode(to);
  };

  // Stripe 側の app を補完合成（profile.app が無いケース対策）
  const { status: stripeStatus } = useStripePlanStatus();
  const userLike: any = {
    ...(userProfile ?? {}),
    app: (userProfile as any)?.app ?? (stripeStatus as any)?.app,
    badges: (userProfile as any)?.badges ?? (stripeStatus as any)?.badges,
    // ルートの override が来る場合に備えフォールバックも残す
    plan_override: (userProfile as any)?.plan_override ?? (stripeStatus as any)?.plan_override,
    plan_override_expires_at:
      (userProfile as any)?.plan_override_expires_at ??
      (stripeStatus as any)?.plan_override_expires_at,
  };

  // フォールバック：profile.app が空の瞬間は Stripe 側の app を補完
if (!userLike?.app && (stripeStatus as any)?.app) {
    (userLike as any).app = (stripeStatus as any).app;
  }

  // ★ 重要：無料UIの基準は「overrideが無効 かつ 実効プランがfree」
  const isFreeUI = !isOverrideActive(userLike) && getEffectivePlan(userLike) === "free";

  // 投稿可否：override中 / facilitatorバッジ有効 / 将来プラン(facilitator/partner)
  //  + 後方互換（allow_howto_post フラグ）

  return (
    <div
      className={`h-screen flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? "is-collapsed" : ""
      }`}
    >
      {/* ヘッダー行（ホームリンク＋折りたたみボタン） */}
      <div
        className={`px-4 pt-4 mb-4 flex items-center ${
          sidebarCollapsed ? "justify-center" : "justify-between"
        }`}
      >
        <button
          onClick={toggleSidebar}
          className="text-sm text-gray-600 hover:text-gray-800 ml-auto"
          aria-expanded={!sidebarCollapsed}
          aria-label={sidebarCollapsed ? "サイドバーを開く" : "サイドバーを閉じる"}
        >
          {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* 本体：スクロール領域 */}
      {!sidebarCollapsed && (
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          <SidebarHeader />
          <SidebarKaitouMonitorLink />

          {/* モードトグル */}
          <div className="flex items-center gap-1 mb-3 rounded-lg bg-gray-100 p-0.5">
            <button
              onClick={() => handleModeSwitch("card")}
              className={`flex-1 text-xs py-1 rounded-md transition-colors ${
                sessionMode === "card"
                  ? "bg-white text-gray-800 shadow-sm font-medium"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              カード式
            </button>
            <button
              onClick={() => handleModeSwitch("text")}
              className={`flex-1 text-xs py-1 rounded-md transition-colors ${
                sessionMode === "text"
                  ? "bg-white text-gray-800 shadow-sm font-medium"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              テキスト式
            </button>
          </div>

          <SidebarCardType
            openSection={openSection}
            setOpenSection={setOpenSection}
            disabled={isFreeUI || sessionMode === "text"}
            showPaidBanner={isFreeUI}
          />
          <SidebarHowTo
            openSection={openSection}
            setOpenSection={setOpenSection}
            disabled={sessionMode === "text"}
          />
          <SidebarTopics
            openSection={openSection}
            setOpenSection={setOpenSection}
          />
          {/* 2026-08-07・Kazuto指摘：将来④「コンテンツ設計」が加わったときの並び順
              （お題リスト→コンテンツ設計→コース）を見越して、お題リストの直後に配置。
              折りたたみ式アコーディオンに変更（単純リンクだと押しても何も起きないように
              見える・開いたら中身が並ぶ方が自然、との実機フィードバックを反映）。 */}
          <SidebarCourses
            openSection={openSection}
            setOpenSection={setOpenSection}
          />
        </div>
      )}

      {/* フッター */}
      {!sidebarCollapsed && <SidebarLegal />}
    </div>
  );
}
